/**
 * Portuguese Home Translations
 * 首页翻译模块 - 葡萄牙语
 * 
 * SEO 关键词覆盖：
 * - aquecedor infravermelho / fabricante / atacado
 * - aquecimento radiante / eficiência energética
 * - aquecedor de varanda / restaurante / oficina
 */

import type { HomeTranslations } from '../../types';

export const ptHome: HomeTranslations = {
  hero: {
    future: 'O Futuro do Aquecimento',
    title: 'Eficiente e Ecológico',
    subtitle: 'Aquecimento Infravermelho',
    description: 'Leve um calor semelhante ao sol para áreas externas. Nossa avançada tecnologia de infravermelho distante oferece calor radiante instantâneo, silencioso e com alta eficiência energética para varandas de restaurantes, jardins, oficinas e áreas de piscina.',
    cta: 'Ver Produtos',
    watchVideo: 'Assistir Vídeo',
    trusted: 'Confiado por mais de 5.000 clientes',
    worldwide: 'Distribuição mundial',
    slides: {
      "slide5": {
        "title": "Feira de Cantão",
        "subtitle": "Encontre-nos em Guangzhou",
        "description": "Encontre a JH Heatsup na Feira de Cantão. Conheça as nossas soluções de aquecimento e explore oportunidades de parceria.",
        "exhibition": {
          "eyebrow": "JH HEATSUP · FEIRA DE CANTÃO",
          "date": "15–19 de outubro de 2026",
          "venue": "Complexo da Feira de Cantão, Guangzhou",
          "boothLabel": "VISITE O NOSSO STAND",
          "booth": "Pavilhão 3.2 · Stand L16"
        }
      },
      "slide4": {
        "title": "Global Sources",
        "subtitle": "Eletrónica de consumo",
        "description": "Encontre a JH Heatsup em Hong Kong. Conheça as nossas soluções de aquecimento e explore oportunidades de parceria.",
        "exhibition": {
          "eyebrow": "JH HEATSUP · FEIRA EM HONG KONG",
          "date": "11–14 de outubro de 2026",
          "venue": "AsiaWorld-Expo, Hong Kong",
          "boothLabel": "VISITE O NOSSO STAND"
        }
      },
      slide1: {
        title: 'Eficiente e Ecológico',
        subtitle: 'Aquecimento Infravermelho',
        description: 'Leve um calor semelhante ao sol para áreas externas. Nossa avançada tecnologia de infravermelho distante oferece calor radiante instantâneo, silencioso e com alta eficiência energética para varandas de restaurantes, jardins, oficinas e áreas de piscina.',
      },
      slide2: {
        title: 'Seguro e Confiável',
        subtitle: 'Feito para o uso diário',
        description: 'Projetados com classificação IPX4 à prova d\'água, proteção contra superaquecimento integrada e materiais duráveis. Nossos aquecedores infravermelhos oferecem calor estável e confiável dia após dia — seguros para funcionar a noite toda.',
      },
      slide3: {
        title: 'Ultra Silencioso',
        subtitle: 'Calor Agradável',
        description: 'Desfrute de um calor radiante silencioso e sem brilho que parece natural, sem ressecar o ar ou circular poeira. Aquecimento infravermelho saudável para lares e escritórios.',
      },
    },
  },
  about: {
    title: 'Sobre JH Aquecedor',
    subtitle: 'Liderando o caminho em tecnologia infravermelha desde 2006',
    intro1: 'Fujian Jinghui Environmental Technology Co., Ltd. (JH Heatsup) é um fabricante líder de aquecedores infravermelhos na China, especializado em aquecedores radiantes de infravermelho distante com alta eficiência energética para uso residencial e comercial.',
    intro2: 'Focamos em P&D, fabricação OEM/ODM e distribuição global para fornecer aquecedores infravermelhos de alta qualidade a compradores atacado em todo o mundo. Nossos produtos possuem certificações CE, CB, SAA e RoHS, exportados para mais de 80 países na Europa, Austrália, Oriente Médio e Sudeste Asiático.',
    stats: {
      yearsExperience: 'Anos de experiência',
      countriesExported: 'Países exportados',
      patents: 'Patentes',
      satisfaction: 'Satisfação',
    },
    ceoQuote: '"Estamos comprometidos em criar um mundo mais verde e acolhedor através da tecnologia."',
    ceoLabel: '- Mensagem do CEO',
    factoryAlt: 'Linha de produção da fábrica de aquecedores infravermelhos JH',
  },
  features: {
    title: 'Por Que Escolher Aquecedores Infravermelhos JH',
    subtitle: 'Nossa tecnologia de infravermelho distante imita o princípio natural de aquecimento do sol, aquecendo objetos e pessoas diretamente em vez de apenas o ar. Isso torna os aquecedores infravermelhos até 80% mais eficientes energeticamente do que aquecedores de convecção tradicionais.',
    energy: {
      title: 'Eficiência Energética',
      desc: 'Até 99% de eficiência de conversão de calor — aquecedores radiantes infravermelhos usam até 80% menos eletricidade que aquecedores de convecção',
    },
    quiet: {
      title: 'Ultra Silencioso',
      desc: 'Design infravermelho sem ventilador, sem luz, sem ruído e sem circulação de poeira',
    },
    safe: {
      title: 'Seguro e Confiável',
      desc: 'Aquecedores infravermelhos à prova d\'água IPX4 com proteção contra superaquecimento integrada, seguros para uso interno e externo',
    },
    smart: {
      title: 'Controle Inteligente',
      desc: 'Controle por aplicativo WiFi Tuya para programação remota de temperatura — economize energia do seu celular',
    },
  },
  categoriesSection: {
    title: 'Compre por categoria',
    subtitle: "Explore nossos aquecedores infravermelhos, portáteis e a gás para encontrar a melhor opção para o seu espaço, de varandas externas a salas de estar.",
    viewAll: 'Ver todos os produtos',
    cards: {
      infrared: {
        badge: 'Popular',
        tagline: 'Aquecedores radiantes de infravermelho distante para calor eficiente e direcionado',
        description: 'Ideal para varandas de restaurantes, oficinas, depósitos e aquecimento interno montado na parede.',
        cta: 'Comprar agora',
      },
      portable: {
        tagline: 'Aquecedores portáteis de fibra de carbono e halógenos para calor flexível',
        description: 'Ótimo para lares, escritórios, aquecimento sob mesa e áreas de entretenimento externo.',
        cta: 'Comprar agora',
      },
      gas: {
        tagline: "Aquecedores a gás e mesas com lareira para áreas ao ar livre",
        description: "Crie um ambiente acolhedor em varandas de restaurantes, jardins e encontros no pátio.",
        cta: "Comprar agora",
      },
    },
  },
  ctaSection: {
    title: 'Pronto para melhorar seu aquecimento?',
    subtitle: 'Entre em contato com a JH Heatsup hoje para preços de atacado, serviços OEM/ODM e uma consulta gratuita.',
    cta: 'Solicitar Orçamento',
  },
};
